import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../../api/axiosInstance";
import { ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import Swal from "sweetalert2";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const toMinutes = (hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return h * 60 + m;
};
const fromMinutes = (mins) => {
  const h = Math.floor(mins / 60).toString().padStart(2, "0");
  const m = (mins % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
};
const toAmPm = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const period = h < 12 ? "AM" : "PM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
};

const AddAppointment = ({ isModal = false, onClose, onSuccess, initialDoctorId, initialDate, initialStartTime }) => {
  const today = dayjs().format("YYYY-MM-DD");
  const allBranchSettings = useSelector((state) => state.business.allBranchSettings);
  const activeBranchId = useSelector((state) => state.business.activeBranchId);
  const [selectedBranchId, setSelectedBranchId] = useState(() => activeBranchId || parseInt(localStorage.getItem('activeBranchId')) || (allBranchSettings.length === 1 ? allBranchSettings[0]?.id : null));

  const [formData, setFormData] = useState(() => {
    const prefillStart = initialStartTime || "";
    const prefillEnd = prefillStart
      ? fromMinutes(Math.min(toMinutes(prefillStart) + 20, 23 * 60 + 50))
      : "";
    return {
      patientId: "",
      doctorId: initialDoctorId || "",
      appointmentDate: initialDate || today,
      startTime: prefillStart,
      endTime: prefillEnd,
      reason: "",
      status: "",
    };
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [patientList, setPatientList] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [timeMode, setTimeMode] = useState("slots");
  const [slotDuration, setSlotDuration] = useState(initialStartTime ? 20 : 5);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [forceScheduleEnabled, setForceScheduleEnabled] = useState(false);
  const [doctorScheduleSlots, setDoctorScheduleSlots] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [isDayOff, setIsDayOff] = useState(false);
  const [reasonSuggestions, setReasonSuggestions]   = useState([]);
  const [showReasonDrop,    setShowReasonDrop]       = useState(false);
  const [commonReasons,     setCommonReasons]        = useState([]);
  const reasonSugTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPatients();
    fetchDoctors();
    axiosInstance.get("getCommonAppointmentReasons")
      .then(res => { if (res.data?.success) setCommonReasons(res.data.data || []) })
      .catch(() => {});
    axiosInstance.get("getModuleSettings")
      .then(res => {
        if (res.data?.success) {
          const fs = res.data.data.find(r => r.module_name === 'force_schedule');
          if (fs) setForceScheduleEnabled(Boolean(fs.enable));
        }
      }).catch(() => {});
  }, []);

  // Fetch doctor's schedule (force_schedule mode)
  useEffect(() => {
    if (!forceScheduleEnabled || !formData.doctorId || !formData.appointmentDate) {
      setDoctorScheduleSlots([]);
      setIsDayOff(false);
      return;
    }
    const d = dayjs(formData.appointmentDate);
    setScheduleLoading(true);
    setDoctorScheduleSlots([]);
    setIsDayOff(false);
    setFormData(prev => ({ ...prev, startTime: '', endTime: '' }));
    axiosInstance.get(`getDoctorDateSchedule/${formData.doctorId}?year=${d.year()}&month=${d.month() + 1}`)
      .then(res => {
        if (res.data?.success) {
          const dateStr = d.format('YYYY-MM-DD');
          const dayEntries = res.data.data.filter(e => (e.schedule_date || '').slice(0, 10) === dateStr);
          const dayOff = dayEntries.some(e => e.is_day_off);
          setIsDayOff(dayOff);
          setDoctorScheduleSlots(dayOff ? [] : dayEntries);
        }
      })
      .catch(() => {})
      .finally(() => setScheduleLoading(false));
  }, [forceScheduleEnabled, formData.doctorId, formData.appointmentDate]);

  // Fetch booked appointments for selected doctor + date
  useEffect(() => {
    if (formData.doctorId && formData.appointmentDate) {
      setLoadingSlots(true);
      axiosInstance
        .post("getAppointmentsByDoctorAndDate", {
          startDate: formData.appointmentDate,
          endDate: formData.appointmentDate,
          doctorIds: [formData.doctorId],
        })
        .then((res) => {
          if (res.data.success) setBookedSlots(res.data.data || []);
        })
        .catch(() => {})
        .finally(() => setLoadingSlots(false));
    } else {
      setBookedSlots([]);
    }
  }, [formData.doctorId, formData.appointmentDate]);

  const fetchPatients = async () => {
    try {
      const res = await axiosInstance.get("getActivePatients");
      if (res.data.success) setPatientList(res.data.data);
    } catch (e) {
      console.error("Error fetching patients:", e);
    }
  };

  const fetchDoctors = async () => {
    try {
      const res = await axiosInstance.get("getActiveDoctors");
      if (res.data.success) setDoctors(res.data.data);
    } catch (e) {
      console.error("Error fetching doctors:", e);
    }
  };

  // Generate all 10-min slots 12:00 AM → 11:50 PM
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let m = 0; m < 24 * 60; m += 10) slots.push(fromMinutes(m));
    return slots;
  }, []);

  const isPastSlot = (slotTime) => {
    if (formData.appointmentDate !== today) return false;
    const now = dayjs();
    return toMinutes(slotTime) < now.hour() * 60 + now.minute();
  };

  const isSlotBooked = (slotTime) => {
    const slotMin = toMinutes(slotTime);
    const slotEnd = slotMin + 10;
    return bookedSlots.some(({ startTime, endTime }) => {
      if (!startTime || !endTime) return false;
      const bStart = toMinutes(startTime.substring(0, 5));
      const bEnd = toMinutes(endTime.substring(0, 5));
      return slotMin < bEnd && slotEnd > bStart;
    });
  };

  const isSlotInRange = (slotTime) => {
    if (!formData.startTime || !formData.endTime) return false;
    const slotMin = toMinutes(slotTime);
    return slotMin >= toMinutes(formData.startTime) && slotMin < toMinutes(formData.endTime);
  };

  const handleSlotClick = (slot) => {
    if (isSlotBooked(slot)) return;
    const startMin = toMinutes(slot);
    const endMin = Math.min(startMin + slotDuration, 23 * 60 + 50);
    setFormData((prev) => ({ ...prev, startTime: slot, endTime: fromMinutes(endMin) }));
    setErrors((prev) => ({ ...prev, startTime: "", endTime: "" }));
    setSubmitError(null);
  };

  const handleDurationSelect = (mins) => {
    setSlotDuration(mins);
    if (formData.startTime) {
      const startMin = toMinutes(formData.startTime);
      const endMin = Math.min(startMin + mins, 23 * 60 + 50);
      setFormData((prev) => ({ ...prev, endTime: fromMinutes(endMin) }));
      setErrors((prev) => ({ ...prev, endTime: "" }));
    }
  };

  const handleReasonChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, reason: value }));
    if (errors.reason) setErrors((prev) => ({ ...prev, reason: "" }));
    clearTimeout(reasonSugTimer.current);
    if (value.trim().length >= 2) {
      reasonSugTimer.current = setTimeout(() => {
        axiosInstance.get(`getAppointmentReasonSuggestions?q=${encodeURIComponent(value.trim())}`)
          .then(res => {
            if (res.data?.success) {
              const filtered = (res.data.data || []).filter(s => s.toLowerCase() !== value.toLowerCase());
              setReasonSuggestions(filtered);
              setShowReasonDrop(filtered.length > 0);
            }
          }).catch(() => {});
      }, 300);
    } else {
      setReasonSuggestions([]);
      setShowReasonDrop(false);
    }
  };

  const applyReason = (text) => {
    setFormData((prev) => ({ ...prev, reason: text }));
    if (errors.reason) setErrors((prev) => ({ ...prev, reason: "" }));
    setReasonSuggestions([]);
    setShowReasonDrop(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    setSubmitError(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.patientId) newErrors.patientId = "Patient is required";
    if (!formData.doctorId) newErrors.doctorId = "Doctor is required";
    if (!formData.appointmentDate) newErrors.appointmentDate = "Date is required";
    if (!formData.reason.trim()) newErrors.reason = "Reason is required";
    if (formData.status !== "Waiting") {
      if (!formData.startTime) newErrors.startTime = "Start time is required";
      if (!formData.endTime) newErrors.endTime = "End time is required";
      if (formData.startTime && formData.endTime) {
        const diff = toMinutes(formData.endTime) - toMinutes(formData.startTime);
        if (diff <= 0) newErrors.endTime = "End time must be after start time";
        else if (diff < 5) newErrors.endTime = "Appointment must be at least 5 minutes";
        else if (diff > 300) newErrors.endTime = "Appointment cannot exceed 5 hours";
      }
    }
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (isDayOff) {
      setSubmitError("Doctor is marked as unavailable (day off) on this date.");
      return;
    }
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setSubmitError("Please fill all required fields.");
      return;
    }
    const dataToSend = { ...formData, branch_id: selectedBranchId || null };
    if (dataToSend.status === "Waiting") {
      delete dataToSend.startTime;
      delete dataToSend.endTime;
    }
    try {
      const res = await axiosInstance.post("createAppointment", dataToSend);
      if (res.data.success) {
        Swal.fire({ icon: "success", title: "Appointment Booked!", text: "Appointment created successfully." });
        if (isModal) {
          onClose?.();
          onSuccess?.();
        } else {
          setTimeout(() => navigate("/Admin/appointment2"), 1500);
        }
      } else {
        setSubmitError(res.data.message || "Failed to create appointment.");
      }
    } catch (error) {
      setSubmitError(error.response?.data?.message || "Something went wrong.");
    }
  };

  // Fetch all doctors' availability for selected date (right panel)
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  useEffect(() => {
    if (!formData.appointmentDate || doctors.length === 0) {
      setAvailableDoctors([]);
      return;
    }
    const d = dayjs(formData.appointmentDate);
    const dateStr = d.format('YYYY-MM-DD');
    setAvailabilityLoading(true);
    Promise.allSettled(
      doctors.map(doc =>
        axiosInstance.get(`getDoctorDateSchedule/${doc.id}?year=${d.year()}&month=${d.month() + 1}`)
          .then(res => {
            if (!res.data?.success) return null;
            const dayEntries = res.data.data.filter(e => (e.schedule_date || '').slice(0, 10) === dateStr);
            if (dayEntries.length === 0) return null;
            const dayOff = dayEntries.some(e => e.is_day_off);
            return { doctor: doc, shifts: dayOff ? [] : dayEntries, isDayOff: dayOff };
          })
      )
    ).then(results => {
      setAvailableDoctors(results.filter(r => r.status === 'fulfilled' && r.value !== null).map(r => r.value));
    }).finally(() => setAvailabilityLoading(false));
  }, [formData.appointmentDate, doctors]);

  const patientOptions = patientList.map((p) => ({
    value: p.id,
    label: `${p.firstName} ${p.lastName}`,
    mobileNumber: p.mobileNumber || "",
    civilIdNumber: p.civilIdNumber || "",
    email: p.email || "",
    age: p.age || "",
  }));

  const doctorOptions = doctors.map((d) => ({ value: d.id, label: d.displayName }));

  const customFilterOption = (option, inputValue) => {
    const s = inputValue.toLowerCase();
    return (
      option.data.label.toLowerCase().includes(s) ||
      option.data.mobileNumber.toLowerCase().includes(s) ||
      option.data.civilIdNumber.toLowerCase().includes(s) ||
      option.data.email.toLowerCase().includes(s) ||
      option.data.age.toString().includes(s)
    );
  };

  const selectStyles = {
    control: (p) => ({ ...p, minHeight: 36, height: 36, fontSize: 13, borderColor: '#d0d5dd', borderRadius: 8, boxShadow: 'none', '&:hover': { borderColor: '#b0b8c4' } }),
    valueContainer: (p) => ({ ...p, height: 36, padding: '0 8px' }),
    indicatorsContainer: (p) => ({ ...p, height: 36 }),
    option: (p) => ({ ...p, fontSize: 13 }),
    singleValue: (p) => ({ ...p, fontSize: 13 }),
    input: (p) => ({ ...p, fontSize: 13, margin: 0, padding: 0 }),
    placeholder: (p) => ({ ...p, fontSize: 13 }),
    menuPortal: (b) => ({ ...b, zIndex: 9999999 }),
    menu: (b) => ({ ...b, zIndex: 999999 }),
    menuList: (b) => ({ ...b, maxHeight: '300px', overflowY: 'auto' }),
  };

  const calendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M8 2v3M16 2v3M3.5 9.09h17M21 8.5V17c0 3-1.5 5-5 5H8c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.695 13.7h.009M15.695 16.7h.009M11.995 13.7h.01M11.995 16.7h.01M8.294 13.7h.01M8.294 16.7h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const isWaiting = formData.status === "Waiting";
  const duration =
    formData.startTime && formData.endTime
      ? toMinutes(formData.endTime) - toMinutes(formData.startTime)
      : null;

  const sectionLabel = (text) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #f0f2f5' }}>
      {text}
    </div>
  );

  const fieldLabel = (text, required) => (
    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
      {text}{required && <span style={{ color: '#e74c3c', marginLeft: 2 }}>*</span>}
    </label>
  );

  const card = (
    <div style={{ background: '#fff', borderRadius: 12, border: isModal ? 'none' : '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => (isModal ? onClose?.() : navigate(-1))}
              title={isModal ? "Close" : "Go back"}
              style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0', background: '#f8f9fb', color: '#5B6B79', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}
              onMouseEnter={e => { e.currentTarget.style.background = '#2CA87F22'; e.currentTarget.style.color = '#2CA87F'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = '#5B6B79'; }}
            >
              <i className={isModal ? "ti ti-x" : "ti ti-arrow-left"} />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#2CA87F15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-calendar-plus" style={{ color: '#2CA87F', fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Book Appointment</div>
              <div style={{ fontSize: 11, color: '#5B6B79' }}>Schedule a new patient appointment</div>
            </div>
          </div>

          {/* ── Body: two columns ── */}
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>

            {/* ── LEFT: Form ── */}
            <div style={{ flex: 1, padding: '24px 28px', minWidth: 0 }}>

              {submitError && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 8, background: '#fff3f3', border: '1px solid #f5c2c7', color: '#842029', fontSize: 12.5, fontWeight: 500, marginBottom: 18 }}>
                  <i className="ti ti-alert-circle" style={{ fontSize: 15, marginTop: 1, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{submitError}</span>
                  <button type="button" onClick={() => setSubmitError(null)} style={{ background: 'none', border: 'none', color: '#842029', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>
                    <i className="ti ti-x" />
                  </button>
                </div>
              )}

              {/* Patient & Doctor */}
              {sectionLabel('Patient & Doctor')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 22 }}>
                <div>
                  {fieldLabel('Patient', true)}
                  <Select
                    classNamePrefix="react-select"
                    className={errors.patientId ? 'is-invalid' : ''}
                    options={patientOptions}
                    value={patientOptions.find((o) => o.value === formData.patientId) || null}
                    onChange={(opt) => handleChange({ target: { name: 'patientId', value: opt ? opt.value : '' } })}
                    placeholder="Search by name, phone, civil ID…"
                    isSearchable
                    filterOption={customFilterOption}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                  {errors.patientId && <div className="invalid-feedback d-block" style={{ fontSize: 11 }}>{errors.patientId}</div>}
                </div>
                <div>
                  {fieldLabel('Doctor', true)}
                  <Select
                    classNamePrefix="react-select"
                    className={errors.doctorId ? 'is-invalid' : ''}
                    options={doctorOptions}
                    value={doctorOptions.find((o) => o.value === formData.doctorId) || null}
                    onChange={(opt) => handleChange({ target: { name: 'doctorId', value: opt ? opt.value : '' } })}
                    placeholder="Select doctor"
                    isSearchable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                  {errors.doctorId && <div className="invalid-feedback d-block" style={{ fontSize: 11 }}>{errors.doctorId}</div>}
                </div>
              </div>

              {/* Date & Type */}
              {sectionLabel('Appointment Details')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 22 }}>
                <div>
                  {fieldLabel('Date', true)}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disablePast
                      value={formData.appointmentDate ? dayjs(formData.appointmentDate) : dayjs()}
                      onChange={(val) => handleChange({ target: { name: 'appointmentDate', value: val ? val.format('YYYY-MM-DD') : '' } })}
                      slots={{ openPickerIcon: calendarIcon }}
                      slotProps={{
                        popper: { sx: { zIndex: 10050 } },
                        textField: {
                          error: !!errors.appointmentDate,
                          helperText: errors.appointmentDate,
                          variant: 'outlined',
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiInputBase-root': { height: 36, fontSize: 13, borderRadius: '8px', backgroundColor: '#fff' },
                            '& .MuiInputBase-input': { fontSize: 13, padding: '0 8px' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d5dd' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b8c4' },
                            '& .MuiInputAdornment-root': { marginLeft: 0 },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
                <div>
                  {fieldLabel('Type')}
                  <div style={{ display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                    {[
                      { value: 'Waiting', label: 'Walk-in', icon: 'ti-door-enter', color: '#f39c12' },
                      { value: 'Emergency', label: 'Emergency', icon: 'ti-urgent', color: '#e74c3c' },
                    ].map(({ value, label, icon, color }) => {
                      const active = formData.status === value;
                      return (
                        <button key={value} type="button"
                          onClick={() => handleChange({ target: { name: 'status', value: active ? '' : value } })}
                          style={{ padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${color}`, background: active ? color : 'transparent', color: active ? '#fff' : color, cursor: 'pointer', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s' }}
                        >
                          <i className={`ti ${icon}`} style={{ fontSize: 13 }} />{label}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>
                    {formData.status === 'Waiting' && 'Walk-in patient — no time slot needed.'}
                    {formData.status === 'Emergency' && 'Urgent — will be prioritised.'}
                    {!formData.status && 'Leave blank for a regular scheduled appointment.'}
                  </div>
                </div>
              </div>

              {/* Time Section */}
              {!isWaiting && (
                <div style={{ marginBottom: 22 }}>
                  {sectionLabel('Time Slot')}
                  <div style={{ border: '1px solid #e9ecef', borderRadius: 10, padding: '14px 16px' }}>

                    {/* Mode toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <div style={{ display: 'flex', borderRadius: 8, border: '1px solid #dee2e6', overflow: 'hidden' }}>
                        {[{ key: 'slots', label: 'Suggest Slots' }, { key: 'manual', label: 'Manual' }].map(({ key, label }) => (
                          <button key={key} type="button" onClick={() => setTimeMode(key)}
                            style={{ padding: '5px 14px', background: timeMode === key ? '#2ca87f' : '#fff', color: timeMode === key ? '#fff' : '#666', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: timeMode === key ? 600 : 400, transition: 'background 0.15s' }}
                          >{label}</button>
                        ))}
                      </div>
                      {(loadingSlots || scheduleLoading) && <span style={{ fontSize: 11, color: '#aaa' }}>Loading…</span>}
                      <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                        {[5, 10, 15, 20, 30, 60].map((mins) => (
                          <button key={mins} type="button" onClick={() => handleDurationSelect(mins)}
                            style={{ padding: '3px 9px', borderRadius: 6, border: `1.5px solid ${slotDuration === mins ? '#2ca87f' : '#dee2e6'}`, background: slotDuration === mins ? '#2ca87f' : '#fff', color: slotDuration === mins ? '#fff' : '#667085', fontSize: 11, fontWeight: slotDuration === mins ? 700 : 500, cursor: 'pointer', transition: 'all 0.1s' }}
                          >{mins === 60 ? '1 Hour' : `${mins} min`}</button>
                        ))}
                      </div>
                    </div>

                    {/* Slot picker */}
                    {timeMode === 'slots' && (
                      !formData.doctorId || !formData.appointmentDate ? (
                        <p style={{ color: '#bbb', fontSize: 12, margin: '4px 0 10px' }}>Select a doctor and date to see available slots.</p>
                      ) : forceScheduleEnabled ? (
                        scheduleLoading ? (
                          <p style={{ color: '#aaa', fontSize: 12, margin: '4px 0 10px' }}>Loading schedule…</p>
                        ) : isDayOff ? (
                          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fff3f3', border: '1px solid #f5c2c7', color: '#842029', fontWeight: 500, fontSize: 12 }}>
                            <i className="ti ti-calendar-off me-2" />Doctor is unavailable (day off) — booking not allowed.
                          </div>
                        ) : doctorScheduleSlots.length === 0 ? (
                          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fffbec', border: '1px solid #fde68a', color: '#92400e', fontWeight: 500, fontSize: 12 }}>
                            <i className="ti ti-calendar-x me-2" />No schedule set for this doctor on this date.
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: 11, color: '#667085', marginBottom: 6 }}>
                              Shift: {doctorScheduleSlots.map(s => `${toAmPm(s.start_time?.slice(0,5))} – ${toAmPm(s.end_time?.slice(0,5))}`).join('  •  ')}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, maxHeight: 160, overflowY: 'auto', paddingBottom: 4 }}>
                              {timeSlots.map((slot) => {
                                const slotMin = toMinutes(slot);
                                const outsideShift = !doctorScheduleSlots.some(s => slotMin >= toMinutes(s.start_time?.slice(0,5)) && slotMin < toMinutes(s.end_time?.slice(0,5)));
                                const booked = isSlotBooked(slot);
                                const past = isPastSlot(slot);
                                const disabled = booked || past || outsideShift;
                                const inRange = isSlotInRange(slot);
                                const isStart = slot === formData.startTime;
                                return (
                                  <button key={slot} type="button" disabled={disabled} onClick={() => handleSlotClick(slot)}
                                    title={booked ? 'Already booked' : past ? 'Time has passed' : outsideShift ? "Outside doctor's shift" : `Select ${toAmPm(slot)}`}
                                    style={{ padding: '3px 9px', borderRadius: 5, border: `1.5px solid ${disabled ? '#e9ecef' : inRange ? '#2ca87f' : '#ddd'}`, background: disabled ? '#f8f9fa' : isStart ? '#2ca87f' : inRange ? '#e6f4ef' : '#fff', color: disabled ? '#ccc' : isStart ? '#fff' : inRange ? '#2ca87f' : '#444', fontSize: 11, cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: isStart ? 700 : 400, textDecoration: booked ? 'line-through' : 'none', transition: 'all 0.1s' }}
                                  >{toAmPm(slot)}</button>
                                );
                              })}
                            </div>
                            <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 10, color: '#999' }}>
                              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#2ca87f', marginRight: 3 }} />Start</span>
                              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#e6f4ef', border: '1px solid #2ca87f', marginRight: 3 }} />Duration</span>
                              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#f8f9fa', border: '1px solid #e9ecef', marginRight: 3 }} />Unavailable</span>
                            </div>
                          </>
                        )
                      ) : (
                        <>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, maxHeight: 160, overflowY: 'auto', paddingBottom: 4 }}>
                            {timeSlots.map((slot) => {
                              const booked = isSlotBooked(slot);
                              const past = isPastSlot(slot);
                              const disabled = booked || past;
                              const inRange = isSlotInRange(slot);
                              const isStart = slot === formData.startTime;
                              return (
                                <button key={slot} type="button" disabled={disabled} onClick={() => handleSlotClick(slot)}
                                  title={booked ? 'Already booked' : past ? 'Time has passed' : `Select ${toAmPm(slot)}`}
                                  style={{ padding: '3px 9px', borderRadius: 5, border: `1.5px solid ${disabled ? '#e9ecef' : inRange ? '#2ca87f' : '#ddd'}`, background: disabled ? '#f8f9fa' : isStart ? '#2ca87f' : inRange ? '#e6f4ef' : '#fff', color: disabled ? '#ccc' : isStart ? '#fff' : inRange ? '#2ca87f' : '#444', fontSize: 11, cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: isStart ? 700 : 400, textDecoration: booked ? 'line-through' : 'none', transition: 'all 0.1s' }}
                                >{toAmPm(slot)}</button>
                              );
                            })}
                          </div>
                          <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 10, color: '#999' }}>
                            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#2ca87f', marginRight: 3 }} />Start</span>
                            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#e6f4ef', border: '1px solid #2ca87f', marginRight: 3 }} />Duration</span>
                            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#f8f9fa', border: '1px solid #e9ecef', marginRight: 3 }} />Booked / Past</span>
                          </div>
                        </>
                      )
                    )}

                    {/* Start / End time inputs */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
                      <div>
                        {fieldLabel('Start Time', true)}
                        <input type="time" className={errors.startTime ? 'is-invalid' : ''} name="startTime" value={formData.startTime} onChange={handleChange} style={{ height: 36, fontSize: 13, border: '1px solid #d0d5dd', borderRadius: 8, padding: '0 10px', width: '100%', outline: 'none', color: '#374151' }} />
                        {errors.startTime && <div className="invalid-feedback" style={{ fontSize: 11 }}>{errors.startTime}</div>}
                      </div>
                      <div>
                        {fieldLabel('End Time', true)}
                        <input type="time" className={errors.endTime ? 'is-invalid' : ''} name="endTime" value={formData.endTime} onChange={handleChange} style={{ height: 36, fontSize: 13, border: '1px solid #d0d5dd', borderRadius: 8, padding: '0 10px', width: '100%', outline: 'none', color: '#374151' }} />
                        {errors.endTime && <div className="invalid-feedback" style={{ fontSize: 11 }}>{errors.endTime}</div>}
                      </div>
                    </div>
                    {duration !== null && duration <= 0 && (
                      <div style={{ fontSize: 11, color: '#e74c3c', marginTop: 5 }}>End time must be after start time</div>
                    )}
                  </div>
                </div>
              )}

              {/* Reason */}
              {sectionLabel('Reason')}
              <div style={{ marginBottom: 24 }}>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="e.g. Follow-up, checkup, dental cleaning…"
                    className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                    name="reason"
                    value={formData.reason}
                    onChange={handleReasonChange}
                    onBlur={() => setTimeout(() => setShowReasonDrop(false), 150)}
                    onFocus={() => reasonSuggestions.length > 0 && setShowReasonDrop(true)}
                    autoComplete="off"
                    style={{ fontSize: 13 }}
                  />
                  {errors.reason && <div className="invalid-feedback" style={{ fontSize: 11 }}>{errors.reason}</div>}
                  {showReasonDrop && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, background: '#fff', border: '1.5px solid #d0d7e2', borderRadius: 7, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', maxHeight: 200, overflowY: 'auto' }}>
                      {reasonSuggestions.map((s, i) => (
                        <div key={i} onMouseDown={() => applyReason(s)}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: i < reasonSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f4f7ff'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                          <i className="ti ti-history" style={{ marginRight: 6, color: '#4680ff' }} />{s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {commonReasons.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                    {commonReasons.map((r, i) => (
                      <button key={i} type="button" onClick={() => applyReason(r)}
                        style={{ background: formData.reason === r ? '#4680ff' : '#f0f3ff', color: formData.reason === r ? '#fff' : '#4680ff', border: `1px solid ${formData.reason === r ? '#4680ff' : '#c5d0f5'}`, borderRadius: 20, padding: '3px 11px', fontSize: 11, cursor: 'pointer', transition: 'all 0.12s', whiteSpace: 'nowrap' }}
                      >{r}</button>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 16, borderTop: '1px solid #f0f2f5' }}>
                <button
                  type="button"
                  onClick={() => (isModal ? onClose?.() : navigate(-1))}
                  style={{ padding: '6px 14px', borderRadius: 20, border: '1.5px solid #5B6B79', background: 'transparent', color: '#5B6B79', cursor: 'pointer', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s' }}
                >
                  <i className="ti ti-x" style={{ fontSize: 13 }} />Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  style={{ padding: '6px 14px', borderRadius: 20, border: '1.5px solid #2CA87F', background: '#2CA87F', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s' }}
                >
                  <i className="ti ti-calendar-check" style={{ fontSize: 13 }} />Book Appointment
                </button>
              </div>
            </div>

            {/* ── RIGHT: Doctor Availability Panel ── */}
            <div style={{ width: 260, flexShrink: 0, borderLeft: '1px solid #e8ecf0', background: '#fafbfc', padding: '24px 18px', alignSelf: 'stretch' }}>

              {/* Branch selector — only shown when clinic has multiple branches */}
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Branch</div>
                  <select
                    value={selectedBranchId || ''}
                    onChange={e => setSelectedBranchId(parseInt(e.target.value) || null)}
                    style={{
                      width: '100%', height: 32, borderRadius: 7, border: '1px solid #dde3eb',
                      padding: '0 8px', fontSize: 12, color: '#1a202c', background: '#fff', outline: 'none',
                    }}
                  >
                    <option value="">— None —</option>
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={b.id}>
                        Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ fontSize: 11, fontWeight: 700, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Doctor Availability</div>
              <div style={{ fontSize: 12, color: '#2CA87F', fontWeight: 600, marginBottom: 14 }}>
                {formData.appointmentDate ? dayjs(formData.appointmentDate).format('ddd, MMM D YYYY') : 'Select a date'}
              </div>

              {availabilityLoading ? (
                <div style={{ color: '#bbb', fontSize: 12 }}>Loading…</div>
              ) : !formData.appointmentDate ? (
                <div style={{ color: '#bbb', fontSize: 12 }}>Select a date to see availability.</div>
              ) : availableDoctors.length === 0 ? (
                <div style={{ color: '#bbb', fontSize: 12 }}>No schedules found for this date.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {availableDoctors.map(({ doctor, shifts, isDayOff: docDayOff }) => {
                    const isSelected = formData.doctorId === String(doctor.id);
                    return (
                      <div
                        key={doctor.id}
                        onClick={() => !docDayOff && !isWaiting && handleChange({ target: { name: 'doctorId', value: String(doctor.id) } })}
                        style={{
                          padding: '10px 12px', borderRadius: 8,
                          border: `1.5px solid ${isSelected ? '#2CA87F' : docDayOff ? '#f5c2c7' : '#e8ecf0'}`,
                          background: isSelected ? '#e8f7f2' : docDayOff ? '#fff3f3' : '#fff',
                          cursor: docDayOff || isWaiting ? 'default' : 'pointer',
                          transition: 'all 0.15s',
                        }}
                        title={docDayOff ? 'Day off' : 'Click to select this doctor'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <i className="ti ti-stethoscope" style={{ fontSize: 13, color: isSelected ? '#2CA87F' : docDayOff ? '#842029' : '#5B6B79' }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: isSelected ? '#1a7a5c' : docDayOff ? '#842029' : '#1a202c', lineHeight: 1.3 }}>{doctor.displayName}</span>
                        </div>
                        {docDayOff ? (
                          <span style={{ fontSize: 10, color: '#842029', background: '#fff3f3', border: '1px solid #f5c2c7', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>Day Off</span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {shifts.map((s, i) => (
                              <span key={i} style={{ fontSize: 10, color: '#2CA87F', background: '#e8f7f2', border: '1px solid #b7e4cf', borderRadius: 4, padding: '1px 6px', fontWeight: 500, display: 'inline-block', width: 'fit-content' }}>
                                {toAmPm(s.start_time?.slice(0,5))} – {toAmPm(s.end_time?.slice(0,5))}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
    </div>
  );

  if (isModal) return card;

  return (
    <div className="pc-container">
      <div className="pc-content">
        {card}
      </div>
      <ToastContainer />
    </div>
  );
};

export default AddAppointment;
